import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Row, Spin, Statistic, Table, Tag, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { api, getAdminErrorMessage } from '../lib/api';

const statusMeta = {
  CREATED: { color: 'blue', text: '已创建' },
  PENDING: { color: 'cyan', text: '处理中' },
  SUCCESS: { color: 'green', text: '成功' },
  FAILED: { color: 'red', text: '失败' },
  CANCELED: { color: 'default', text: '已取消' }
};

const typeMeta = {
  DEPOSIT: { color: 'green', text: '充值' },
  WITHDRAW: { color: 'orange', text: '提现' }
};

const priorityStyle = {
  LOW: { text: '低' },
  NORMAL: { text: '普通' },
  HIGH: { text: '高' },
  URGENT: { text: '紧急' }
};

const HomeDashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportTotal, setSupportTotal] = useState(0);
  const [supportLoading, setSupportLoading] = useState(false);
  const [loadedAt, setLoadedAt] = useState('');

  const todayRange = useMemo(() => getTodayRange(), []);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = () => {
    fetchTodayPayments();
    fetchOpenSupportTickets();
  };

  const fetchTodayPayments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/payments', {
        params: {
          createdFrom: todayRange.start,
          createdTo: todayRange.end,
          page: 1,
          pageSize: 500,
          sortBy: 'createdAt',
          sortDirection: 'desc'
        }
      });
      setOrders(response.data.items || []);
      setTotal(response.data.total || 0);
      setLoadedAt(formatClock(new Date()));
    } catch (error) {
      message.error(getAdminErrorMessage(error, '加载今日支付信息失败'));
    } finally {
      setLoading(false);
    }
  };

  const fetchOpenSupportTickets = async () => {
    setSupportLoading(true);
    try {
      const response = await api.get('/api/admin/support/tickets', {
        params: {
          status: 'OPEN',
          page: 1,
          pageSize: 5,
          sortBy: 'lastReplyAt',
          sortDirection: 'desc'
        }
      });
      setSupportTickets(response.data.items || []);
      setSupportTotal(response.data.total || 0);
    } catch (error) {
      message.error(getAdminErrorMessage(error, '加载客服工单失败'));
    } finally {
      setSupportLoading(false);
    }
  };

  const summary = useMemo(() => buildSummary(orders), [orders]);

  const columns = [
    {
      title: '时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (value) => formatDateTime(value)
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 90,
      render: (value) => {
        const meta = typeMeta[value] || { color: 'default', text: value || '-' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (value) => {
        const meta = statusMeta[value] || { color: 'default', text: value || '-' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (value) => `¥${Number(value || 0).toFixed(2)}`
    },
    {
      title: '渠道',
      dataIndex: 'channel',
      key: 'channel',
      width: 100,
      render: paymentChannelLabel
    },
    {
      title: '订单号',
      dataIndex: 'outTradeNo',
      key: 'outTradeNo',
      ellipsis: true
    },
    {
      title: '目标账户',
      dataIndex: 'targetAccount',
      key: 'targetAccount',
      ellipsis: true,
      render: (value) => value || '-'
    }
  ];

  const supportColumns = [
    {
      title: '工单ID',
      dataIndex: 'id',
      key: 'id',
      width: 90,
    },
    {
      title: '用户',
      dataIndex: 'username',
      key: 'username',
      width: 120,
    },
    {
      title: '标题',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 90,
      render: (value) => {
        const meta = priorityStyle[value] || priorityStyle.NORMAL;
        return <Tag color={getPriorityTagColor(value)}>{meta.text}</Tag>;
      }
    },
    {
      title: '最后回复',
      dataIndex: 'lastReplyAt',
      key: 'lastReplyAt',
      width: 150,
      render: (value) => formatDateTime(value)
    }
  ];

  return (
    <div style={{ padding: '8px', minHeight: '100%' }}>
      <div style={pageHeaderStyle}>
        <div>
          <div style={titleStyle}>今日支付</div>
          <div style={subtitleStyle}>
            {todayRange.label} 的充值、提现、支付订单与待处理客服概览{loadedAt ? ` · ${loadedAt} 更新` : ''}
          </div>
        </div>
        <div style={headerActionsStyle}>
          <Button onClick={fetchDashboardData} loading={loading || supportLoading}>
            刷新
          </Button>
          <Button type="primary" onClick={() => navigate('/dashboard/payments')}>
            查看支付管理
          </Button>
          <Button onClick={() => navigate('/dashboard/support')}>
            处理客服工单
          </Button>
        </div>
      </div>

      <Spin spinning={loading || supportLoading}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} lg={6}>
            <MetricCard title="今日订单" value={total} suffix="笔" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <MetricCard title="成功金额" value={summary.successAmount} precision={2} prefix="¥" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <MetricCard title="充值金额" value={summary.depositAmount} precision={2} prefix="¥" />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <MetricCard title="提现金额" value={summary.withdrawAmount} precision={2} prefix="¥" />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={10}>
            <Card title="状态分布" style={cardStyle}>
              <div style={statusGridStyle}>
                <StatusCount label="成功" value={summary.statusCounts.SUCCESS} color="#16a34a" />
                <StatusCount label="处理中" value={summary.statusCounts.PENDING} color="#0284c7" />
                <StatusCount label="已创建" value={summary.statusCounts.CREATED} color="#4f46e5" />
                <StatusCount label="异常" value={summary.statusCounts.FAILED + summary.statusCounts.CANCELED} color="#dc2626" />
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={14}>
            <Card title="运营提示" style={cardStyle}>
              <div style={hintGridStyle}>
                <HintItem label="待处理订单" value={summary.statusCounts.CREATED + summary.statusCounts.PENDING} />
                <HintItem label="失败/取消订单" value={summary.statusCounts.FAILED + summary.statusCounts.CANCELED} />
                <HintItem label="净流入" value={`¥${(summary.depositAmount - summary.withdrawAmount).toFixed(2)}`} />
                <HintItem label="待处理客服工单" value={supportTotal} />
              </div>
              {total > orders.length && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 14 }}
                  message={`今日共有 ${total} 笔订单，当前首页只聚合最近 ${orders.length} 笔。请进入支付管理分页核对完整明细。`}
                />
              )}
            </Card>
          </Col>
        </Row>

        <Card
          title="待处理客服工单"
          extra={<Button type="link" onClick={() => navigate('/dashboard/support')}>进入客服工单</Button>}
          style={{ ...cardStyle, marginBottom: 16 }}
        >
          <Table
            columns={supportColumns}
            dataSource={supportTickets}
            rowKey="id"
            loading={supportLoading}
            pagination={false}
            scroll={{ x: 650 }}
            onRow={() => ({
              onClick: () => navigate('/dashboard/support')
            })}
          />
        </Card>

        <Card title="今日订单明细" style={cardStyle}>
          <Table
            columns={columns}
            dataSource={orders}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: true }}
            scroll={{ x: 900 }}
          />
        </Card>
      </Spin>
    </div>
  );
};

const MetricCard = ({ title, value, prefix, suffix, precision }) => (
  <Card style={cardStyle}>
    <Statistic title={title} value={value} prefix={prefix} suffix={suffix} precision={precision} />
  </Card>
);

const StatusCount = ({ label, value, color }) => (
  <div style={statusItemStyle}>
    <div style={{ ...statusDotStyle, backgroundColor: color }} />
    <div>
      <div style={statusValueStyle}>{value}</div>
      <div style={statusLabelStyle}>{label}</div>
    </div>
  </div>
);

const HintItem = ({ label, value }) => (
  <div style={hintItemStyle}>
    <div style={hintLabelStyle}>{label}</div>
    <div style={hintValueStyle}>{value}</div>
  </div>
);

const buildSummary = (orders) => {
  const initial = {
    successAmount: 0,
    depositAmount: 0,
    withdrawAmount: 0,
    statusCounts: {
      CREATED: 0,
      PENDING: 0,
      SUCCESS: 0,
      FAILED: 0,
      CANCELED: 0
    }
  };

  return orders.reduce((summary, order) => {
    const amount = Number(order.amount || 0);
    if (summary.statusCounts[order.status] !== undefined) {
      summary.statusCounts[order.status] += 1;
    }
    if (order.status === 'SUCCESS') {
      summary.successAmount += amount;
      if (order.type === 'DEPOSIT') {
        summary.depositAmount += amount;
      } else if (order.type === 'WITHDRAW') {
        summary.withdrawAmount += amount;
      }
    }
    return summary;
  }, initial);
};

const getTodayRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
  return {
    start: toLocalDateTime(start),
    end: toLocalDateTime(end),
    label: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
  };
};

const toLocalDateTime = (date) => (
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
);

const formatClock = (date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;

const formatDateTime = (value) => {
  if (!value) return '-';
  return String(value).replace('T', ' ').slice(0, 16);
};

const pad = (value) => String(value).padStart(2, '0');

const paymentChannelLabel = (value) => ({
  ALIPAY: '支付宝',
  WECHAT: '微信',
  BANK_CARD: '银行卡'
}[value] || value || '-');

const getPriorityTagColor = (value) => ({
  LOW: 'default',
  NORMAL: 'blue',
  HIGH: 'orange',
  URGENT: 'red'
}[value] || 'blue');

const pageHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 16,
  marginBottom: 20,
  flexWrap: 'wrap'
};

const titleStyle = {
  fontSize: 28,
  fontWeight: 700,
  color: '#1f2a44',
  marginBottom: 6
};

const subtitleStyle = {
  fontSize: 14,
  color: '#6b7280'
};

const headerActionsStyle = {
  display: 'flex',
  gap: 10,
  flexWrap: 'wrap'
};

const cardStyle = {
  borderRadius: 8,
  boxShadow: '0 14px 30px rgba(31, 42, 68, 0.06)'
};

const statusGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 14
};

const statusItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  backgroundColor: '#f8fbff',
  borderRadius: 8
};

const statusDotStyle = {
  width: 10,
  height: 10,
  borderRadius: 5
};

const statusValueStyle = {
  fontSize: 18,
  fontWeight: 700,
  color: '#1f2a44'
};

const statusLabelStyle = {
  fontSize: 12,
  color: '#6b7280'
};

const hintGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 12
};

const hintItemStyle = {
  padding: 14,
  backgroundColor: '#f8fbff',
  border: '1px solid #e6eefc',
  borderRadius: 8
};

const hintLabelStyle = {
  color: '#6b7280',
  fontSize: 12,
  marginBottom: 8
};

const hintValueStyle = {
  color: '#1f2a44',
  fontSize: 18,
  fontWeight: 700
};

export default HomeDashboard;
